/* NVN Marine — theme.js
 *
 * Deferred, vanilla. HTML-first; JS only as needed (Dawn principle).
 * Behaviours covered:
 *   - cart drawer open/close + Section Rendering API for live cart updates
 *   - mobile menu drawer
 *   - quantity steppers
 *   - predictive search dropdown
 *   - PDP gallery thumbs + sticky mobile CTA
 *   - recently-viewed via localStorage
 *   - same-day-ship countdown to 3 PM EST
 *   - utility-bar mobile rotating ticker
 *   - filter chip live update on PLP
 *   - article TOC builder
 *   - copy-link share button
 *
 * HTML returned by fetch() is parsed via DOMParser so we never assign untrusted
 * strings to innerHTML.
 */
(function () {
  'use strict';

  document.documentElement.dataset.themeReady = '1';

  /* -- mobile class -------------------------------------------------------- */
  const mq = window.matchMedia('(max-width: 989px)');
  function syncMobileClass(e) { document.body.classList.toggle('is-mobile', e.matches); }
  syncMobileClass(mq);
  mq.addEventListener('change', syncMobileClass);

  /* -- helpers ------------------------------------------------------------ */
  const $$ = (sel, ctx) => Array.from((ctx || document).querySelectorAll(sel));
  const $ = (sel, ctx) => (ctx || document).querySelector(sel);

  function parseHTML(htmlString) {
    return new DOMParser().parseFromString(htmlString, 'text/html');
  }

  function replaceChildren(target, sourceNode) {
    while (target.firstChild) target.removeChild(target.firstChild);
    if (!sourceNode) return;
    Array.from(sourceNode.childNodes).forEach((n) => target.appendChild(n));
  }

  /* -- drawer (shared cart + mobile) -------------------------------------- */
  let lastFocusBeforeDrawer = null;
  let drawerFocusTrapHandler = null;

  function getFocusable(container) {
    return Array.from(container.querySelectorAll(
      'a[href], button:not([disabled]), input:not([disabled]):not([type=hidden]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])'
    )).filter((el) => el.offsetParent !== null);
  }

  function trapFocus(el) {
    drawerFocusTrapHandler = (e) => {
      if (e.key !== 'Tab') return;
      const focusables = getFocusable(el);
      if (!focusables.length) {
        e.preventDefault();
        el.focus();
        return;
      }
      const first = focusables[0];
      const last = focusables[focusables.length - 1];
      if (e.shiftKey && document.activeElement === first) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && document.activeElement === last) {
        e.preventDefault();
        first.focus();
      }
    };
    document.addEventListener('keydown', drawerFocusTrapHandler);
  }

  function openDrawer(el) {
    if (!el) return;
    lastFocusBeforeDrawer = document.activeElement;
    el.removeAttribute('hidden');
    el.setAttribute('aria-hidden', 'false');
    requestAnimationFrame(() => {
      el.classList.add('open');
      const focusables = getFocusable(el);
      if (focusables.length) focusables[0].focus({ preventScroll: true });
      else el.focus({ preventScroll: true });
      trapFocus(el);
    });
    document.documentElement.style.overflow = 'hidden';
    /* view_cart only fires on /cart page (matches live theme); not on drawer */
  }

  function closeDrawer(el) {
    if (!el) return;
    el.classList.remove('open');
    el.setAttribute('aria-hidden', 'true');
    if (drawerFocusTrapHandler) {
      document.removeEventListener('keydown', drawerFocusTrapHandler);
      drawerFocusTrapHandler = null;
    }
    setTimeout(() => el.setAttribute('hidden', ''), 280);
    document.documentElement.style.overflow = '';
    if (lastFocusBeforeDrawer && typeof lastFocusBeforeDrawer.focus === 'function') {
      lastFocusBeforeDrawer.focus({ preventScroll: true });
      lastFocusBeforeDrawer = null;
    }
  }

  document.addEventListener('click', (e) => {
    const close = e.target.closest('[data-drawer-close]');
    if (close) {
      const drawer = close.closest('[data-cart-drawer], [data-mobile-drawer]');
      if (drawer) closeDrawer(drawer);
    }
  });
  document.addEventListener('keydown', (e) => {
    if (e.key === 'Escape') {
      $$('[data-cart-drawer].open, [data-mobile-drawer].open').forEach(closeDrawer);
    }
    if ((e.metaKey || e.ctrlKey) && e.key === 'k') {
      e.preventDefault();
      const input = document.querySelector('[data-predictive-search-input]');
      if (input) { input.focus(); input.select(); }
    }
  });

  /* -- type-anywhere search (BOATiD-style) -------------------------------- */
  document.addEventListener('keydown', (e) => {
    if (e.metaKey || e.ctrlKey || e.altKey) return;
    if (e.key.length !== 1) return;
    const active = document.activeElement;
    if (active && (active.tagName === 'INPUT' || active.tagName === 'TEXTAREA' || active.isContentEditable)) return;
    const input = document.querySelector('[data-predictive-search-input]');
    if (!input) return;
    input.focus();
    // character lands naturally after focus since keypress fires on the focused element
  });

  /* -- cart drawer toggle from header ------------------------------------- */
  document.addEventListener('click', (e) => {
    const trigger = e.target.closest('[data-cart-toggle]');
    if (!trigger) return;
    e.preventDefault();
    openDrawer($('[data-cart-drawer]'));
    loadCartRecommendations();
  });

  /* -- cart recommendations (lazy-loaded on drawer open) ------------------ */
  let recsLoaded = false;
  async function loadCartRecommendations() {
    const wrap = $('[data-cart-recs]');
    if (!wrap || recsLoaded) return;
    const productId = wrap.dataset.productId;
    const limit = parseInt(wrap.dataset.limit || '4', 10);
    if (!productId) return;
    try {
      const url = `${window.Shopify?.routes?.root || '/'}recommendations/products.json?product_id=${productId}&limit=${limit}&intent=related`;
      const res = await fetch(url);
      if (!res.ok) return;
      const data = await res.json();
      const products = (data && data.products) || [];
      if (!products.length) {
        wrap.setAttribute('hidden', '');
        return;
      }
      const grid = wrap.querySelector('[data-cart-recs-grid]');
      if (!grid) return;
      const moneyFormat = (cents) => {
        const usd = (cents / 100).toFixed(2);
        return '$' + usd;
      };
      const html = products.map((p) => {
        const img = (p.featured_image && (typeof p.featured_image === 'string' ? p.featured_image : p.featured_image.url)) || (p.images && p.images[0]) || '';
        const variantId = p.variants && p.variants[0] && p.variants[0].id;
        return `
          <div class="rec-card">
            <a href="${p.url}" class="rec-card-link">
              <img class="rec-card-img" src="${img}" alt="${p.title}" loading="lazy">
              <div class="rec-card-brand">${p.vendor || ''}</div>
              <div class="rec-card-title">${p.title}</div>
              <div class="rec-card-price">${moneyFormat(p.price)}</div>
            </a>
            ${variantId ? `<button type="button" class="rec-card-add" data-rec-add data-variant-id="${variantId}">Add</button>` : ''}
          </div>
        `;
      }).join('');
      const doc = parseHTML(`<div>${html}</div>`);
      replaceChildren(grid, doc.body.firstChild);
      wrap.removeAttribute('hidden');
      recsLoaded = true;
    } catch (err) {
      console.warn('[cart] recs failed', err);
    }
  }

  document.addEventListener('click', async (e) => {
    const btn = e.target.closest('[data-rec-add]');
    if (!btn) return;
    const variantId = parseInt(btn.dataset.variantId, 10);
    if (!variantId) return;
    btn.disabled = true;
    btn.textContent = 'Adding…';
    try {
      const res = await fetch(`${window.Shopify?.routes?.root || '/'}cart/add.js`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ id: variantId, quantity: 1 })
      });
      if (res.ok) {
        const added = await res.json();
        trackAddToCart(added);
        recsLoaded = false;
        await refreshCart();
      }
    } catch (_) {}
    btn.disabled = false;
    btn.textContent = 'Add';
  });

  /* -- mobile menu toggle ------------------------------------------------- */
  document.addEventListener('click', (e) => {
    const trigger = e.target.closest('[data-mobile-toggle]');
    if (!trigger) return;
    e.preventDefault();
    openDrawer($('[data-mobile-drawer]'));
  });

  /* -- quantity steppers -------------------------------------------------- */
  document.addEventListener('click', (e) => {
    const dec = e.target.closest('[data-qty-dec]');
    const inc = e.target.closest('[data-qty-inc]');
    if (!dec && !inc) return;
    const stepper = (dec || inc).closest('[data-qty-stepper]');
    const input = stepper && stepper.querySelector('[data-qty-input]');
    if (!input) return;
    const cur = parseInt(input.value, 10) || 0;
    const next = dec ? Math.max(parseInt(input.min, 10) || 0, cur - 1) : cur + 1;
    input.value = next;
    input.dispatchEvent(new Event('change', { bubbles: true }));
  });

  /* -- cart updates via Section Rendering API -----------------------------
   * Always refreshes the cart drawer. On /cart we ALSO refresh the
   * main-cart section so line prices, subtotals, totals etc. update
   * after a quantity change or remove. */
  async function refreshCart() {
    const onCartPage = location.pathname === '/cart' || location.pathname.indexOf('/cart?') === 0;
    const sections = ['cart-drawer'];
    if (onCartPage) sections.push(getCartPageSectionId());
    const url = (window.Shopify?.routes?.root || '/') + '?sections=' + sections.filter(Boolean).join(',');
    try {
      const res = await fetch(url);
      if (!res.ok) return;
      const data = await res.json();

      // Drawer refresh
      const drawerHTML = data['cart-drawer'];
      if (typeof drawerHTML === 'string') {
        const doc = parseHTML(drawerHTML);
        const next = doc.querySelector('[data-cart-drawer]');
        const cur = $('[data-cart-drawer]');
        if (next && cur) {
          const wasOpen = cur.classList.contains('open');
          cur.replaceWith(next);
          if (wasOpen) openDrawer(next);
        }
      }

      // Cart page refresh — swap in the new main-cart contents in place
      if (onCartPage) {
        const sectionId = getCartPageSectionId();
        const cartPageHTML = sectionId && data[sectionId];
        if (typeof cartPageHTML === 'string') {
          const doc = parseHTML(cartPageHTML);
          const next = doc.querySelector('.shopify-section--main-cart, [id^="shopify-section-"]');
          const cur = document.getElementById('shopify-section-' + sectionId);
          if (next && cur) cur.innerHTML = next.innerHTML;
        }
      }

      // Update header count
      const cartRes = await fetch('/cart.js');
      if (cartRes.ok) {
        const cart = await cartRes.json();
        const count = $('[data-cart-count]');
        if (count) {
          count.textContent = cart.item_count;
          count.toggleAttribute('hidden', cart.item_count === 0);
        }
      }
    } catch (err) {
      console.warn('[cart] refresh failed', err);
    }
  }

  function getCartPageSectionId() {
    // Shopify wraps each section with id="shopify-section-<sectionId>". Find
    // the main-cart wrapper on /cart and extract its section id.
    const wrap = document.querySelector('.shopify-section--main-cart');
    if (!wrap) return null;
    const m = wrap.id && wrap.id.match(/^shopify-section-(.+)$/);
    return m ? m[1] : null;
  }

  /* -- product form submit (add to cart) ---------------------------------- */
  /* Capture phase + stopImmediatePropagation: Shopify's Web Pixel Manager also
   * delegates 'submit' on document (bubble phase), and it fired add_to_cart the
   * moment the form was submitted — BEFORE our Ajax /cart/add resolved. That
   * counted out-of-stock and errored adds (cart stayed empty, GA4/Ads still
   * recorded add_to_cart). Handling in capture phase and stopping the event
   * means wpm never sees the raw submit; the correct add_to_cart fires from the
   * successful /cart/add.js Ajax call instead. NOTE: stopImmediatePropagation
   * also silences any OTHER app listening to this form's submit — all our ad
   * pixels are native (sandboxed web pixels, unaffected), but re-check if a new
   * app is added. */
  document.addEventListener('submit', async (e) => {
    const form = e.target.closest('[data-product-form] form, form#PDPForm');
    if (!form) return;
    const submitBtn = e.submitter;
    if (submitBtn && submitBtn.name === 'checkout') return; // let it submit normally
    e.preventDefault();
    e.stopImmediatePropagation();
    const data = new FormData(form);
    try {
      const res = await fetch(`${window.Shopify?.routes?.root || '/'}cart/add.js`, {
        method: 'POST',
        body: data,
        headers: { Accept: 'application/javascript', 'X-Requested-With': 'XMLHttpRequest' }
      });
      if (!res.ok) throw new Error('Add to cart failed');
      const added = await res.json();
      /* /cart/add.js returns the line's NEW total quantity, not the amount just
       * added — pass the requested qty so add_to_cart value isn't inflated. */
      const addedQty = parseInt(data.get('quantity'), 10) || 1;
      trackAddToCart(added, addedQty);
      await refreshCart();
      openDrawer($('[data-cart-drawer]'));
    } catch (err) {
      console.warn('[cart] add failed', err);
    }
  }, true);

  /* -- analytics helpers --------------------------------------------------- */
  /* Every item_id uses the compound `shopify_us_<product_id>_<variant_id>` form
   * for consistency across the funnel. All ad/analytics pixels are owned by
   * native Shopify apps / channels — the theme only pushes vendor-neutral
   * events to window.dataLayer. */
  var _currency = (window.Shopify && window.Shopify.currency && window.Shopify.currency.active) || 'USD';
  /* GA4 + Google Ads conversions are sent exclusively by the Shopify Google &
   * YouTube channel web pixel — the theme never calls gtag() directly. */

  function _compoundId(pid, vid) { return 'shopify_us_' + pid + '_' + vid; }

  function _cartItemFull(i) {
    return {
      item_id: _compoundId(i.product_id, i.variant_id),
      item_variant: i.variant_title && i.variant_title !== 'Default Title' ? i.variant_title : 'Default Title',
      item_name: i.product_title || i.title || '',
      price: ((i.price || 0) / 100).toFixed(2),
      item_brand: i.vendor || null,
      item_category: i.product_type || null,
      item_list_name: null,
      quantity: i.quantity,
      discount: i.line_level_discount_allocations && i.line_level_discount_allocations.length
        ? (i.line_level_discount_allocations.reduce(function(s,d){return s+(d.amount||0)},0)/100).toFixed(2)
        : null
    };
  }

  function trackViewCart() {
    /* Live theme only fires view_cart on /cart, NOT on drawer open. Same here. */
    if (location.pathname.indexOf('/cart') !== 0) return;
    try {
      fetch('/cart.js').then(function(r) { return r.json(); }).then(function(cart) {
        window.dataLayer = window.dataLayer || [];
        window.dataLayer.push({
          pageType: 'Cart',
          event: 'view_cart',
          ecommerce: {
            currency: _currency,
            value: (cart.total_price || 0) / 100,
            items: (cart.items || []).map(_cartItemFull)
          }
        });
      }).catch(function() {});
    } catch (_) {}
  }

  function trackRemoveFromCart(line) {
    try {
      var d = line.dataset;
      if (!d.itemId) return;
      var compoundId = d.itemId.indexOf('shopify_us_') === 0
        ? d.itemId
        : _compoundId(d.itemProductId || '', d.itemVariantId || '');
      var price = parseFloat(d.itemPrice || 0);
      var qty = parseInt(d.itemQty || 1, 10);
      var value = price * qty;
      window.dataLayer = window.dataLayer || [];
      /* GTM ecommerce push */
      window.dataLayer.push({
        event: 'remove_from_cart',
        ecommerce: {
          currency: _currency,
          value: value,
          items: [{
            item_id: compoundId,
            item_name: d.itemName || '',
            item_brand: d.itemBrand || null,
            item_category: d.itemCategory || null,
            item_variant: d.itemVariant || null,
            item_list_name: null,
            price: price.toFixed(2),
            quantity: qty,
            imageURL: d.itemImageUrl || null,
            productURL: d.itemProductUrl || null,
            sku: d.itemSku || null
          }]
        }
      });
    } catch (_) {}
  }

  function trackBeginCheckout() {
    try {
      fetch('/cart.js').then(function(r) { return r.json(); }).then(function(cart) {
        window.dataLayer = window.dataLayer || [];
        window.dataLayer.push({
          event: 'begin_checkout',
          ecommerce: {
            currency: _currency,
            value: (cart.total_price || 0) / 100,
            items: _cartItems(cart)
          }
        });
        // Meta / TikTok / Bing pixels are owned by their native Shopify apps.
      }).catch(function() {});
    } catch (_) {}
  }

  function trackSelectItem(link) {
    try {
      var card = link.closest('[data-product-card]');
      if (!card) return;
      var listEl = link.closest('[data-item-list-name]');
      var d = card.dataset;
      var listName = listEl ? (listEl.dataset.itemListName || '') : '';
      var listId = listEl ? (listEl.dataset.itemListId || '') : '';
      window.dataLayer = window.dataLayer || [];
      window.dataLayer.push({
        event: 'select_item',
        ecommerce: {
          item_list_name: listName,
          item_list_id: listId,
          items: [{
            item_id: d.itemId || '',
            item_name: d.itemName || '',
            item_brand: d.itemBrand || null,
            item_category: d.itemCategory || null,
            item_variant: d.itemVariant || null,
            item_list_name: listName,
            item_list_id: listId,
            price: d.itemPrice ? parseFloat(d.itemPrice).toFixed(2) : '0.00',
            index: parseInt(d.index || 0, 10),
            imageURL: d.itemImageUrl || null,
            productURL: d.itemProductUrl || null,
            sku: d.itemSku || null
          }]
        }
      });
    } catch (_) {}
  }

  /* -- analytics: add_to_cart (dataLayer only) ----------------------------- */
  /* No gtag()/conversion calls here: GA4 + Google Ads add_to_cart conversions
   * are sent once, by the Shopify Google & YouTube channel web pixel. Firing
   * them from the theme too was the source of 2-3x Google conversion counting. */
  function trackAddToCart(payload, addedQty) {
    try {
      var pid = payload.product_id || payload.id;
      var vid = payload.variant_id || payload.id;
      var compoundId = _compoundId(pid, vid);
      var price = (payload.price || payload.line_price || 0) / 100;
      /* Use the quantity actually added, not payload.quantity (which is the
       * line's new cart total) — otherwise value over-counts re-adds. */
      var qty = addedQty || 1;
      var value = price * qty;
      var name = payload.product_title || payload.title || '';
      var sku = payload.sku || null;
      var imageURL = (payload.featured_image && (payload.featured_image.url || payload.featured_image)) || payload.image || null;
      var productURL = payload.url || (payload.handle ? '/products/' + payload.handle : null);

      window.dataLayer = window.dataLayer || [];

      /* GTM ecommerce push — full item shape for tags that read from ecommerce.items[] */
      window.dataLayer.push({
        event: 'add_to_cart',
        ecommerce: {
          currency: _currency,
          value: value,
          items: [{
            item_id: compoundId,
            item_name: name,
            item_brand: payload.vendor || null,
            item_category: payload.product_type || null,
            item_variant: payload.variant_title && payload.variant_title !== 'Default Title' ? payload.variant_title : null,
            item_list_name: null,
            price: price.toFixed(2),
            discount: payload.compare_at_price > payload.price ? ((payload.compare_at_price - payload.price) / 100).toFixed(2) : null,
            quantity: qty,
            affiliation: 'NVN Marine',
            imageURL: imageURL,
            productURL: productURL,
            sku: sku
          }]
        }
      });

      /* Ad/analytics pixels (Meta, TikTok, Bing, Matomo) are owned by native
         Shopify apps / channels — the theme only pushes to the dataLayer. */
    } catch (_) { /* swallow analytics errors */ }
  }

  /* -- cart line qty change ----------------------------------------------- */
  document.addEventListener('change', async (e) => {
    const input = e.target.closest('[data-cart-line] [data-qty-input]');
    if (!input) return;
    const line = input.closest('[data-cart-line]');
    const key = line && line.dataset.lineKey;
    if (!key) return;
    try {
      await fetch(`${window.Shopify?.routes?.root || '/'}cart/change.js`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ id: key, quantity: parseInt(input.value, 10) || 0 })
      });
      await refreshCart();
    } catch (err) {
      console.warn('[cart] change failed', err);
    }
  });

  /* -- cart line remove --------------------------------------------------- */
  document.addEventListener('click', async (e) => {
    const remove = e.target.closest('[data-cart-remove]');
    if (!remove) return;
    e.preventDefault();
    const line = remove.closest('[data-cart-line]');
    const key = line && line.dataset.lineKey;
    if (!key) return;
    if (line) trackRemoveFromCart(line);
    try {
      await fetch(`${window.Shopify?.routes?.root || '/'}cart/change.js`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ id: key, quantity: 0 })
      });
      await refreshCart();
    } catch (err) {
      console.warn('[cart] remove failed', err);
    }
  });

  /* -- begin_checkout ------------------------------------------------------- */
  document.addEventListener('click', (e) => {
    if (e.target.closest('[name="checkout"]')) trackBeginCheckout();
  });

  /* -- select_item (product card clicks) ----------------------------------- */
  document.addEventListener('click', (e) => {
    const link = e.target.closest('[data-product-card] a[href*="/products/"]');
    if (link) trackSelectItem(link);
  });

  /* -- view_promotion (IntersectionObserver on [data-promo-id] banners) ---- */
  (function () {
    function promoPayload(el) {
      return {
        promotion_id: el.dataset.promoId || '',
        promotion_name: el.dataset.promoName || '',
        creative_name: el.dataset.promoCreative || '',
        creative_slot: el.dataset.promoSlot || ''
      };
    }
    var promoEls = document.querySelectorAll('[data-promo-id]');
    if (!promoEls.length) return;
    if ('IntersectionObserver' in window) {
      var obs = new IntersectionObserver(function(entries) {
        entries.forEach(function(entry) {
          if (!entry.isIntersecting) return;
          obs.unobserve(entry.target);
          window.dataLayer = window.dataLayer || [];
          window.dataLayer.push({
            event: 'view_promotion',
            ecommerce: { items: [promoPayload(entry.target)] }
          });
        });
      }, { threshold: 0.5 });
      promoEls.forEach(function(el) { obs.observe(el); });
    }
    /* select_promotion — any CTA click inside a promo banner */
    document.addEventListener('click', function(e) {
      var banner = e.target.closest('[data-promo-id]');
      var cta = banner && e.target.closest('a, button');
      if (!banner || !cta) return;
      window.dataLayer = window.dataLayer || [];
      window.dataLayer.push({
        event: 'select_promotion',
        ecommerce: { items: [promoPayload(banner)] }
      });
    });
  })();

  /* -- predictive search: handled by assets/nvn-autocomplete.js ----------- */

  /* -- PDP gallery thumbs (image + YouTube) ------------------------------- */
  document.addEventListener('click', (e) => {
    const thumb = e.target.closest('[data-pdp-thumb]');
    if (!thumb) return;
    const wrap = thumb.closest('[data-pdp-thumbs]');
    const main = thumb.closest('[data-gallery]')?.querySelector('[data-gallery-main]');
    if (!wrap || !main) return;
    wrap.querySelectorAll('[data-pdp-thumb].active').forEach((t) => t.classList.remove('active'));
    thumb.classList.add('active');

    const type = thumb.dataset.thumbType || 'image';
    if (type === 'video') {
      const vid = thumb.dataset.videoId;
      if (!vid) return;
      // Replace main with a YouTube embed (lite-style: instant by user gesture).
      replaceChildren(main, null);
      const wrapEl = document.createElement('div');
      wrapEl.className = 'yt-embed';
      const iframe = document.createElement('iframe');
      iframe.src = `https://www.youtube-nocookie.com/embed/${encodeURIComponent(vid)}?autoplay=1&rel=0&modestbranding=1`;
      iframe.title = 'Product video';
      iframe.setAttribute('frameborder', '0');
      iframe.setAttribute('allow', 'accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share');
      iframe.setAttribute('allowfullscreen', '');
      iframe.referrerPolicy = 'strict-origin-when-cross-origin';
      wrapEl.appendChild(iframe);
      main.appendChild(wrapEl);
    } else {
      const fullSrc = thumb.dataset.imageSrc;
      const alt = thumb.dataset.imageAlt || '';
      replaceChildren(main, null);
      const img = document.createElement('img');
      img.src = fullSrc || (thumb.querySelector('img')?.getAttribute('src') || '').replace(/_\d+x\d+\./, '_1400x.');
      img.alt = alt;
      img.loading = 'eager';
      main.appendChild(img);
      // Re-add zoom button
      const btn = document.createElement('button');
      btn.type = 'button';
      btn.className = 'gallery-zoom';
      btn.setAttribute('aria-label', 'Zoom');
      btn.dataset.galleryZoom = '';
      btn.innerHTML = '<svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" stroke-width="1.75" stroke-linecap="round" stroke-linejoin="round"><circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/><line x1="11" y1="8" x2="11" y2="14"/><line x1="8" y1="11" x2="14" y2="11"/></svg>';
      main.appendChild(btn);
    }
  });

  /* -- PDP gallery zoom modal -------------------------------------------- */
  document.addEventListener('click', (e) => {
    const zoom = e.target.closest('[data-gallery-zoom]');
    if (!zoom) return;
    const main = zoom.closest('[data-gallery-main]');
    const img = main && main.querySelector('img');
    if (!img) return;
    const overlay = document.createElement('div');
    overlay.className = 'zoom-overlay';
    overlay.setAttribute('role', 'dialog');
    overlay.setAttribute('aria-label', 'Image zoom');
    overlay.style.cssText = 'position:fixed;inset:0;background:rgba(7,16,28,0.92);z-index:10000;display:flex;align-items:center;justify-content:center;cursor:zoom-out;padding:20px';
    const big = document.createElement('img');
    big.src = (img.src || '').replace(/_\d+x\d+\./, '_2000x.').replace(/width=\d+/, 'width=2000');
    big.alt = img.alt || '';
    big.style.cssText = 'max-width:100%;max-height:100%;object-fit:contain';
    overlay.appendChild(big);
    overlay.addEventListener('click', () => overlay.remove());
    document.body.appendChild(overlay);
    document.addEventListener('keydown', function escClose(ev) {
      if (ev.key === 'Escape') {
        overlay.remove();
        document.removeEventListener('keydown', escClose);
      }
    });
  });

  /* -- PDP sticky mobile CTA visibility ----------------------------------- */
  const mobileBuy = $('[data-mobile-buy]');
  const mainBuy = $('form#PDPForm');
  if (mobileBuy && mainBuy) {
    const obs = new IntersectionObserver(([entry]) => {
      mobileBuy.classList.toggle('visible', !entry.isIntersecting);
    }, { rootMargin: '0px 0px -100px 0px' });
    obs.observe(mainBuy);
  }

  /* -- recently viewed (localStorage) ------------------------------------- */
  const RV_KEY = 'nvn_recent';
  const RV_MAX = 8;
  function trackView() {
    const handleEl = $('meta[name="product-handle"]');
    const handle = handleEl?.content || (location.pathname.match(/\/products\/([^/?#]+)/) || [])[1];
    if (!handle) return;
    let list = [];
    try { list = JSON.parse(localStorage.getItem(RV_KEY) || '[]'); } catch (_) {}
    list = [handle, ...list.filter((h) => h !== handle)].slice(0, RV_MAX);
    localStorage.setItem(RV_KEY, JSON.stringify(list));
  }
  async function renderRecent() {
    const wrap = $('[data-recently-viewed]');
    if (!wrap) return;
    let list = [];
    try { list = JSON.parse(localStorage.getItem(RV_KEY) || '[]'); } catch (_) {}
    const currentHandle = (location.pathname.match(/\/products\/([^/?#]+)/) || [])[1];
    list = list.filter((h) => h !== currentHandle).slice(0, 4);
    if (!list.length) return;
    const grid = wrap.querySelector('[data-recently-viewed-grid]');
    if (!grid) return;
    try {
      const fragments = await Promise.all(list.map(async (h) => {
        const r = await fetch(`/products/${h}?view=product-card-fragment`);
        if (!r.ok) return null;
        const text = await r.text();
        return parseHTML(text).body;
      }));
      replaceChildren(grid, null);
      fragments.forEach((frag) => {
        if (frag) Array.from(frag.childNodes).forEach((n) => grid.appendChild(n));
      });
      wrap.removeAttribute('hidden');
    } catch (_) {}
  }
  if (location.pathname.includes('/products/')) trackView();
  renderRecent();

  /* -- same-day-ship countdown ------------------------------------------- */
  function tickCutoff() {
    const clocks = $$('[data-cutoff-clock]');
    if (!clocks.length) return;
    const cutoffHour = parseInt(clocks[0].dataset.cutoffHour || '15', 10);
    const now = new Date();
    const nyParts = new Intl.DateTimeFormat('en-US', {
      timeZone: 'America/New_York', hour12: false,
      hour: '2-digit', minute: '2-digit', second: '2-digit'
    }).formatToParts(now).reduce((acc, p) => (acc[p.type] = p.value, acc), {});
    const h = parseInt(nyParts.hour, 10);
    const m = parseInt(nyParts.minute, 10);
    const s = parseInt(nyParts.second, 10);
    let secsLeft = (cutoffHour * 3600) - (h * 3600 + m * 60 + s);
    if (secsLeft < 0) secsLeft += 24 * 3600;
    const hh = String(Math.floor(secsLeft / 3600)).padStart(2, '0');
    const mm = String(Math.floor((secsLeft % 3600) / 60)).padStart(2, '0');
    const ss = String(secsLeft % 60).padStart(2, '0');
    const txt = `${hh}:${mm}:${ss}`;
    clocks.forEach((el) => (el.textContent = txt));

    const stamp = `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')} EST`;
    $$('[data-bulletin-stamp], [data-clock-stamp]').forEach((el) => (el.textContent = stamp));
  }
  tickCutoff();
  setInterval(tickCutoff, 1000);

  /* -- utility-bar rotating ticker (mobile) ------------------------------- */
  const ticker = $('[data-ticker]');
  if (ticker) {
    const msgs = ticker.querySelectorAll('[data-ticker-msg]');
    if (msgs.length > 1) {
      let i = 0;
      setInterval(() => {
        msgs[i].classList.remove('on');
        i = (i + 1) % msgs.length;
        msgs[i].classList.add('on');
      }, 3200);
    }
  }

  /* -- PLP filter live submit -------------------------------------------- */
  let filterTimer;
  document.addEventListener('change', (e) => {
    if (!e.target.closest('[data-filter-input]')) return;
    const form = e.target.closest('[data-filter-form]');
    if (!form) return;
    clearTimeout(filterTimer);
    filterTimer = setTimeout(() => {
      const params = new URLSearchParams();
      form.querySelectorAll('[data-filter-input]:checked, [data-filter-input]:not([type=checkbox])').forEach((el) => {
        if (el.type === 'checkbox') {
          if (el.checked) params.append(el.name, el.value);
        } else if (el.value) {
          params.append(el.name, el.value);
        }
      });
      const url = window.location.pathname + (params.toString() ? '?' + params.toString() : '');
      window.location.href = url;
    }, 150);
  });

  /* -- PLP grid/list view toggle ----------------------------------------- */
  const PLP_VIEW_KEY = 'nvn_plp_view';
  function applyPlpView(mode) {
    const grid = $('[data-plp-grid]');
    const list = $('[data-plp-list]');
    if (!grid || !list) return;
    if (mode === 'list') {
      grid.setAttribute('hidden', '');
      list.removeAttribute('hidden');
    } else {
      list.setAttribute('hidden', '');
      grid.removeAttribute('hidden');
    }
    $$('[data-view-toggle]').forEach((btn) => {
      const pressed = btn.dataset.viewToggle === mode;
      btn.setAttribute('aria-pressed', pressed ? 'true' : 'false');
    });
  }
  // Restore from localStorage on load
  try {
    const stored = localStorage.getItem(PLP_VIEW_KEY);
    if (stored === 'list' || stored === 'grid') applyPlpView(stored);
  } catch (_) {}
  document.addEventListener('click', (e) => {
    const btn = e.target.closest('[data-view-toggle]');
    if (!btn) return;
    const mode = btn.dataset.viewToggle;
    applyPlpView(mode);
    try { localStorage.setItem(PLP_VIEW_KEY, mode); } catch (_) {}
  });

  /* -- article TOC builder ----------------------------------------------- */
  const tocList = $('[data-article-toc-list]');
  const prose = $('[data-article-prose]');
  if (tocList && prose) {
    const headings = prose.querySelectorAll('h2, h3');
    headings.forEach((h, idx) => {
      if (!h.id) h.id = `h-${idx}-${(h.textContent || '').toLowerCase().replace(/[^a-z0-9]+/g, '-').slice(0, 40)}`;
      const li = document.createElement('li');
      const a = document.createElement('a');
      a.href = `#${h.id}`;
      a.textContent = h.textContent;
      li.appendChild(a);
      tocList.appendChild(li);
    });
    if (!headings.length) tocList.parentElement?.setAttribute('hidden', '');
  }

  /* -- warehouse stock widget -------------------------------------------- */
  (function loadWarehouseStock() {
    const el = document.querySelector('[data-warehouse-sku]');
    if (!el) return;
    const sku = el.dataset.warehouseSku;
    if (!sku) return;
    fetch(`https://search.nvnmarine.com/i/${encodeURIComponent(sku)}`)
      .then(r => r.ok ? r.text() : Promise.reject())
      .then(html => {
        // Parse state: qty pairs from whatever HTML the API returns
        const doc = (new DOMParser()).parseFromString(html, 'text/html');
        const rows = [];
        let total = 0;
        const parseRow = txt => {
          const m = txt.trim().match(/^([A-Za-z\s]+):\s*(\d+)/);
          if (m) { rows.push({ state: m[1].trim(), qty: parseInt(m[2]) }); total += parseInt(m[2]); }
        };
        const items = doc.querySelectorAll('li, tr, div, span, p');
        if (items.length) {
          items.forEach(n => parseRow(n.textContent));
        } else {
          doc.body.textContent.split(/[\n,•·]+/).forEach(parseRow);
        }
        if (!rows.length) return;

        const truck = `<svg viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="var(--nvn-orange)" stroke-width="1.75" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><rect x="1" y="3" width="15" height="13"/><polygon points="16 8 20 8 23 11 23 16 16 16 16 8"/><circle cx="5.5" cy="18.5" r="2.5"/><circle cx="18.5" cy="18.5" r="2.5"/></svg>`;
        const grid = rows.map(r =>
          `<div class="wh-row"><span class="wh-state">${r.state}:</span><span class="wh-qty">${r.qty}</span></div>`
        ).join('');

        el.innerHTML = `
          <div class="wh-header">
            <div class="wh-header-left">${truck}<span>Available stock:</span></div>
            <span class="wh-total">${total}</span>
          </div>
          <div class="wh-grid">${grid}</div>`;
        el.hidden = false;
      })
      .catch(() => {});
  })();

  /* -- Dynamic pickup-day labels (Eastern business calendar) ------------- */
  /* Refine the server-rendered pickup day against the live clock so cached
   * pages stay correct. Each label/day element carries data-pickup-mode:
   *   fl        in stock at the FL store  → "Today" on a weekday, "Monday" on
   *             the weekend (the store is open Mon–Fri only).
   *   transfer  in stock elsewhere        → "Next-Day", or "Monday"/"Tuesday"
   *             around the weekend (3pm cutoff), so pickup lands on a business
   *             day: after 3pm Thu→Fri 3pm = Monday; after 3pm Fri→Mon 9am =
   *             Tuesday; otherwise Next-Day.
   * Computed in America/New_York so the boundaries are correct for every
   * visitor regardless of their own timezone. */
  (function refinePickupDay() {
    const labels = document.querySelectorAll('[data-pickup-label]');
    const days = document.querySelectorAll('[data-pickup-day]');
    if (!labels.length && !days.length) return;
    const et = new Date(new Date().toLocaleString('en-US', { timeZone: 'America/New_York' }));
    const dow = et.getDay();   // 0 Sun … 6 Sat
    const hr = et.getHours();  // 0-23, Eastern
    function flDay() {
      return (dow === 0 || dow === 6) ? 'Monday' : 'Today';   // store open Mon–Fri
    }
    function transferDay() {
      if ((dow === 4 && hr >= 15) || (dow === 5 && hr < 15)) return 'Monday';
      if ((dow === 5 && hr >= 15) || dow === 6 || dow === 0 || (dow === 1 && hr < 9)) return 'Tuesday';
      return 'Next-Day';
    }
    function dayFor(mode) { return mode === 'fl' ? flDay() : transferDay(); }
    labels.forEach(function (el) { el.textContent = 'Pickup ' + dayFor(el.dataset.pickupMode); });
    days.forEach(function (el) { el.textContent = dayFor(el.dataset.pickupMode); });
  })();

  /* -- Same-day upgrade when the FL store has the item in-house ----------- */
  /* Server-side Liquid can't read FL per-location quantity, so the badge and
   * the in-store block default to next-day pickup. The in-house feed
   * (search.nvnmarine.com/i/{sku}) emits <!-- inhouse available --> when the
   * Fort Lauderdale retail store physically has the item — the reliable FL
   * stock signal. When present, upgrade to same-day pickup: "Today" on a
   * weekday, "Monday" on the weekend (the store is open Mon–Fri). */
  (function flagInhousePickup() {
    const badge = document.querySelector('[data-pickup-badge][data-pickup-sku]');
    const store = document.querySelector('[data-local-store][data-pickup-sku]');
    const sku = (badge && badge.dataset.pickupSku) || (store && store.dataset.pickupSku);
    if (!sku) return;
    fetch('https://search.nvnmarine.com/i/' + encodeURIComponent(sku))
      .then(function (r) { return r.ok ? r.text() : Promise.reject(); })
      .then(function (html) {
        if (!/<!--\s*inhouse available\s*-->/i.test(html)) return;
        const et = new Date(new Date().toLocaleString('en-US', { timeZone: 'America/New_York' }));
        const dow = et.getDay();
        const day = (dow === 0 || dow === 6) ? 'Monday' : 'Today';   // store open Mon–Fri
        if (badge) {
          const label = badge.querySelector('[data-pickup-label]');
          if (label) label.textContent = 'Pickup ' + day;
        }
        if (store) {
          // It's physically in the store now → "In stock for pickup … (day)".
          const status = store.querySelector('[data-pickup-status]');
          if (status) status.textContent = 'In stock for pickup at our Fort Lauderdale store (' + day + ')';
        }
      })
      .catch(function () {});
  })();

  /* -- PDP section scroll-to nav: highlight active section --------------- */
  (function initPdpSecNav() {
    const nav = document.querySelector('[data-pdp-sec-nav]');
    if (!nav) return;
    const links = Array.from(nav.querySelectorAll('.pdp-sec-nav-link'));
    const sections = Array.from(document.querySelectorAll('[data-pdp-section]'));
    if (!links.length || !sections.length) return;

    const setActive = id => {
      links.forEach(l => l.classList.toggle('active', l.dataset.navTarget === id));
      const activeLink = links.find(l => l.dataset.navTarget === id);
      if (activeLink && nav.scrollWidth > nav.clientWidth) {
        // Only scroll the NAV BAR horizontally — never touch page scroll
        nav.scrollLeft = activeLink.offsetLeft - (nav.clientWidth / 2) + (activeLink.offsetWidth / 2);
      }
    };

    // Passive scroll listener — no IntersectionObserver (avoids scroll fighting)
    let ticking = false;
    const onScroll = () => {
      if (ticking) return;
      ticking = true;
      requestAnimationFrame(() => {
        const offset = 100; // sticky header clearance
        let currentId = null;
        for (const s of sections) {
          if (s.getBoundingClientRect().top <= offset) currentId = s.id;
        }
        if (currentId) setActive(currentId);
        ticking = false;
      });
    };
    window.addEventListener('scroll', onScroll, { passive: true });
    onScroll();

    links.forEach(l => l.addEventListener('click', e => {
      e.preventDefault();
      const target = document.getElementById(l.dataset.navTarget);
      if (target) target.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }));
  })();

  /* -- filter checkbox instant visual feedback ---------------------------- */
  document.addEventListener('change', (e) => {
    if (e.target.type !== 'checkbox' || !e.target.hasAttribute('data-filter-input')) return;
    const label = e.target.closest('.filter-check');
    if (label) label.classList.toggle('is-active', e.target.checked);
  });

  /* -- collection SEO description toggle ---------------------------------- */
  document.addEventListener('click', (e) => {
    const btn = e.target.closest('[data-seo-toggle]');
    if (!btn) return;
    const body = btn.nextElementSibling;
    if (!body) return;
    const expanded = btn.getAttribute('aria-expanded') === 'true';
    btn.setAttribute('aria-expanded', !expanded);
    if (expanded) { body.setAttribute('hidden', ''); } else { body.removeAttribute('hidden'); }
  });

  /* -- copy-link share button -------------------------------------------- */
  document.addEventListener('click', async (e) => {
    const btn = e.target.closest('[data-copy-link]');
    if (!btn) return;
    e.preventDefault();
    try {
      await navigator.clipboard.writeText(btn.dataset.url || location.href);
      const old = btn.getAttribute('aria-label');
      btn.setAttribute('aria-label', 'Link copied');
      setTimeout(() => btn.setAttribute('aria-label', old), 1600);
      const label = btn.querySelector('[data-copy-label]');
      if (label) {
        const origText = label.textContent;
        label.textContent = '✓ Copied!';
        btn.classList.add('is-copied');
        setTimeout(() => { label.textContent = origText; btn.classList.remove('is-copied'); }, 1600);
      }
    } catch (_) {}
  });

  /* -- recover password toggle on login page ----------------------------- */
  document.addEventListener('click', (e) => {
    const t = e.target.closest('[data-recover-toggle]');
    if (!t) return;
    e.preventDefault();
    const target = $('#recover');
    if (target) {
      target.toggleAttribute('hidden');
      if (!target.hidden) target.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  });

  /* -- Quick-add button on product cards ----------------------------------- */
  document.addEventListener('click', async (e) => {
    const btn = e.target.closest('[data-quick-add]');
    if (!btn) return;
    e.preventDefault();
    e.stopPropagation();
    const variantId = btn.dataset.variantId;
    if (!variantId) return;
    btn.classList.add('loading');
    try {
      const res = await fetch(`${window.Shopify?.routes?.root || '/'}cart/add.js`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ id: parseInt(variantId, 10), quantity: 1 })
      });
      if (!res.ok) throw new Error('add failed');
      const added = await res.json();
      await refreshCart();
      btn.classList.remove('loading');
      btn.classList.add('added');
      btn.textContent = '✓ Added';
      setTimeout(() => {
        btn.classList.remove('added');
        btn.innerHTML = '<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><circle cx="9" cy="21" r="1"/><circle cx="20" cy="21" r="1"/><path d="M1 1h4l2.68 13.39a2 2 0 001.94 1.61h9.72a2 2 0 001.94-1.61L23 6H6"/></svg> Add to cart';
      }, 1800);
      openDrawer($('[data-cart-drawer]'));
      trackAddToCart(added);
    } catch (err) {
      btn.classList.remove('loading');
      console.warn('[quick-add] failed', err);
    }
  });

  /* -- FBT add-to-cart bundle -------------------------------------------- */
  document.addEventListener('click', async (e) => {
    const btn = e.target.closest('[data-fbt-add]');
    if (!btn) return;
    const root = btn.closest('[data-fbt]');
    const items = $$('[data-fbt-row] [data-variant-id]', root)
      .map((el) => ({ id: parseInt(el.dataset.variantId, 10), quantity: 1 }));
    if (!items.length) return;
    try {
      await fetch(`${window.Shopify?.routes?.root || '/'}cart/add.js`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ items })
      });
      await refreshCart();
      openDrawer($('[data-cart-drawer]'));
    } catch (err) {
      console.warn('[fbt] add failed', err);
    }
  });

  /* -- view_cart on /cart page load (mirrors live theme) ------------------ */
  if (location.pathname === '/cart' || location.pathname.indexOf('/cart?') === 0) {
    trackViewCart();
  }

  /* -- PDP description style sanitiser -----------------------------------
   * Many product descriptions arrive with an embedded <style> block that
   * declares unscoped global selectors (e.g. `h2 { color: #0056b3 }`).
   * Those rules leak out of the description and override our theme's
   * heading colour everywhere on the page. Strategy: scope every selector
   * in those style blocks under `.lede` so the rules only ever match
   * elements inside the description. Anything that selects body/html is
   * dropped entirely. Removing the styles outright would also work but
   * scoping preserves the description author's intent (tables, accent
   * boxes, etc.) without polluting the rest of the page.
   */
  (function sanitizePdpDescription() {
    var lede = document.querySelector('[data-pdp-description]');
    if (!lede) return;
    var styles = lede.querySelectorAll('style');
    if (!styles.length) return;
    styles.forEach(function (style) {
      try {
        var src = style.textContent || '';
        // Replace each `selector {` with a scoped variant
        var scoped = src.replace(/([^{}]+)\{/g, function (m, sel) {
          // Leave at-rules (@media, @keyframes, @font-face) intact
          if (/^\s*@/.test(sel)) return sel + '{';
          var parts = sel.split(',').map(function (s) { return s.trim(); }).filter(Boolean);
          var rewritten = parts.map(function (s) {
            // Drop selectors that would clobber whole-page elements
            if (/^(html|body|:root)\b/.test(s)) return null;
            // Already scoped under our wrapper — leave it
            if (/^\.lede\b/.test(s) || /^\[data-pdp-description\]/.test(s)) return s;
            // Otherwise prefix with .lede
            return '.lede ' + s;
          }).filter(Boolean);
          return rewritten.join(', ') + ' {';
        });
        style.textContent = scoped;
      } catch (_) { /* if scoping fails, drop the block so it can't leak */
        style.remove();
      }
    });
  })();
})();
